/**
 * The Salvation Team console's person dialogs: add a person, log what happened, correct details, assign or correct the
 * Lead / Backup, and the reason dialogs for reopen, close and review. Every save refreshes the person, the lists and
 * the overview. Failures show inline (and as a toast), never only in the console.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  SALVATION_CONVERT_STATUS as S,
  SALVATION_ERROR_CODES as E,
  SALVATION_FOLLOW_UP_TYPE as F,
  salvationIsFollowUp,
  useSalvationAdminService,
  type SalvationConvertDetailView,
} from '@actschurch/ccms-sdk';
import { Alert, Anchor, Button, Group, Modal, Radio, RadioGroup, SegmentedControl, Select, SimpleGrid, Stack, Text, TextInput, Textarea } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  duplicateConvertId,
  salvationErrorCode,
  salvationErrorMessage,
  salvationKeys,
  salvationPaths,
  todayYmd,
} from './salvationUtils';
import { lookupOptions, useSalvationLookups, useSalvationSettings, useSalvationTeamMembers, useSalvationTeams } from './useSalvationData';

/** Refreshes everything a change to one person can affect. */
function useRefreshPerson() {
  const queryClient = useQueryClient();
  return (convertId?: number) => {
    if (convertId) queryClient.invalidateQueries({ queryKey: salvationKeys.person(convertId) });
    queryClient.invalidateQueries({ queryKey: salvationKeys.peopleRoot() });
    queryClient.invalidateQueries({ queryKey: [...salvationKeys.all, 'overview'] });
    queryClient.invalidateQueries({ queryKey: salvationKeys.teams() });
    queryClient.invalidateQueries({ queryKey: [...salvationKeys.all, 'members'] });
  };
}

// ---------------------------------------------------------------- add a person

interface AddPersonModalProps {
  opened: boolean;
  onClose: () => void;
  defaultTeamId?: number | null;
  canAssign: boolean;
  onAdded: (person: SalvationConvertDetailView) => void;
}

const emptyAdd = { firstName: '', lastName: '', phone: '', email: '', decisionDate: '', decisionSourceID: null as string | null, note: '' };

export function AddPersonModal({ opened, onClose, defaultTeamId, canAssign, onAdded }: AddPersonModalProps) {
  const admin = useSalvationAdminService();
  const refresh = useRefreshPerson();
  const { data: lookups } = useSalvationLookups();
  const teams = useSalvationTeams();
  const [teamId, setTeamId] = useState<string | null>(defaultTeamId ? String(defaultTeamId) : null);
  const [form, setForm] = useState(emptyAdd);
  const [pairMode, setPairMode] = useState<'auto' | 'choose'>('auto');
  const [lead, setLead] = useState<string | null>(null);
  const [backup, setBackup] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<{ message: string; convertId?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const members = useSalvationTeamMembers(teamId ? Number(teamId) : null);

  useEffect(() => {
    if (!opened) return;
    setForm({ ...emptyAdd, decisionDate: todayYmd() });
    setPairMode('auto');
    setLead(null);
    setBackup(null);
    setDuplicate(null);
    setError(null);
    setTeamId(defaultTeamId ? String(defaultTeamId) : null);
  }, [opened, defaultTeamId]);

  useEffect(() => {
    if (!teamId && teams.options.length === 1) setTeamId(teams.options[0].value);
  }, [teamId, teams.options]);

  const save = useMutation({
    mutationFn: (confirmNotDuplicate: boolean) =>
      admin.addPerson({
        teamID: teamId ? Number(teamId) : null,
        firstName: form.firstName,
        lastName: form.lastName || null,
        phone: form.phone || null,
        email: form.email || null,
        decisionDate: form.decisionDate || null,
        decisionSourceID: form.decisionSourceID ? Number(form.decisionSourceID) : null,
        leadTeamMemberID: pairMode === 'choose' && lead ? Number(lead) : null,
        backupTeamMemberID: pairMode === 'choose' && backup ? Number(backup) : null,
        note: form.note || null,
        confirmNotDuplicate,
      }),
    onSuccess: (person) => {
      refresh(person.convertID);
      toast.success('Added', person.lead ? `${person.lead.displayName} is their Lead.` : 'Give them a Lead and a Backup when the team has two members.');
      onAdded(person);
      onClose();
    },
    onError: (err) => {
      if (salvationErrorCode(err) === E.DuplicateConvert) {
        setDuplicate({ message: (err as { message?: string }).message ?? 'Someone with this phone number or email is already on the Salvation Team.', convertId: duplicateConvertId(err) });
        return;
      }
      setError(salvationErrorMessage(err));
      toast.error("Couldn't add the person", salvationErrorMessage(err));
    },
  });

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const canSave = !!teamId && form.firstName.trim().length > 0 && (form.phone.trim() || form.email.trim())
    && (pairMode === 'auto' || (lead && backup && lead !== backup));

  return (
    <Modal opened={opened} onClose={onClose} title="Add a person" size="lg">
      <Stack gap="sm">
        <Select label="Campus team" data={teams.options} value={teamId} onChange={setTeamId} required placeholder="Pick the campus team" />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="First name" value={form.firstName} onChange={(e) => set({ firstName: e.currentTarget.value })} required maxLength={100} />
          <TextInput label="Surname" value={form.lastName} onChange={(e) => set({ lastName: e.currentTarget.value })} maxLength={100} />
          <TextInput label="Phone" placeholder="082 123 4567" value={form.phone} onChange={(e) => set({ phone: e.currentTarget.value })} maxLength={30} />
          <TextInput label="Email" value={form.email} onChange={(e) => set({ email: e.currentTarget.value })} maxLength={256} />
          <TextInput label="Decision date" type="date" value={form.decisionDate} max={todayYmd()} onChange={(e) => set({ decisionDate: e.currentTarget.value })} />
          <Select label="Where they decided" data={lookupOptions(lookups?.decisionSources)} value={form.decisionSourceID} onChange={(v) => set({ decisionSourceID: v })} clearable />
        </SimpleGrid>
        <Text size="xs" c="dimmed">A phone number or an email address is needed.</Text>

        {canAssign && (
          <Stack gap={6}>
            <Text size="sm" fw={600}>Lead and Backup</Text>
            <SegmentedControl
              value={pairMode}
              onChange={(v) => setPairMode(v as 'auto' | 'choose')}
              data={[
                { value: 'auto', label: 'Share out by load' },
                { value: 'choose', label: 'Choose them' },
              ]}
            />
            {pairMode === 'choose' && (
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Select label="Lead" data={members.options} value={lead} onChange={setLead} searchable required disabled={!teamId} />
                <Select label="Backup" data={members.options.filter((o) => o.value !== lead)} value={backup} onChange={setBackup} searchable required disabled={!teamId} />
              </SimpleGrid>
            )}
          </Stack>
        )}

        <Textarea label="Note (optional)" placeholder="For example: prefers evening calls" value={form.note} onChange={(e) => set({ note: e.currentTarget.value })} autosize minRows={2} maxLength={500} />

        {duplicate && (
          <Alert color="orange" title="Is this the same person?">
            <Stack gap={6}>
              <Text size="sm">{duplicate.message}</Text>
              <Group gap="sm">
                {duplicate.convertId && (
                  <Anchor component={Link} to={salvationPaths.person(duplicate.convertId)} size="sm">
                    Open the existing person
                  </Anchor>
                )}
                <Button size="xs" variant="light" color="orange" loading={save.isPending} onClick={() => save.mutate(true)}>
                  It's someone else, add anyway
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}
        {error && <Alert color="red">{error}</Alert>}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { setError(null); setDuplicate(null); save.mutate(false); }} loading={save.isPending} disabled={!canSave}>
            Add
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------- log what happened

interface LogFollowUpModalProps {
  person: SalvationConvertDetailView;
  opened: boolean;
  onClose: () => void;
}

export function LogFollowUpModal({ person, opened, onClose }: LogFollowUpModalProps) {
  const admin = useSalvationAdminService();
  const refresh = useRefreshPerson();
  const { data: lookups } = useSalvationLookups();
  const { data: settings } = useSalvationSettings();
  const [typeId, setTypeId] = useState<string>('');
  const [note, setNote] = useState('');
  const [confirmHandOver, setConfirmHandOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const maxNote = settings?.noteMaxLength ?? 280;
  const inFollowUp = salvationIsFollowUp(person.statusID);

  const types = useMemo(
    () => (lookups?.followUpTypes ?? []).filter((t) => t.volunteerCanLog && t.isActive && (inFollowUp || t.id === F.Note)),
    [lookups, inFollowUp]
  );

  useEffect(() => {
    if (!opened) return;
    setTypeId(inFollowUp ? '' : String(F.Note));
    setNote('');
    setConfirmHandOver(false);
    setError(null);
  }, [opened, inFollowUp]);

  const save = useMutation({
    mutationFn: (confirmHandOverWithoutMeeting: boolean) =>
      admin.logFollowUp(person.convertID, { typeID: Number(typeId), note: note || null, confirmHandOverWithoutMeeting }),
    onSuccess: () => {
      refresh(person.convertID);
      toast.success('Saved', 'The follow-up is in their history.');
      onClose();
    },
    onError: (err) => {
      if (salvationErrorCode(err) === E.HandOverNeedsMet) {
        setConfirmHandOver(true);
        return;
      }
      setError(salvationErrorMessage(err));
      toast.error("Couldn't save the follow-up", salvationErrorMessage(err));
    },
  });

  const needsNote = Number(typeId) === F.Note;
  return (
    <Modal opened={opened} onClose={onClose} title={`What happened with ${person.firstName}?`} size="md">
      <Stack gap="sm">
        {!inFollowUp && <Text size="sm" c="dimmed">They are past follow-up, so only a note can be added. Reopen them to change where they are.</Text>}
        <RadioGroup value={typeId} onChange={setTypeId}>
          <Stack gap={8}>
            {types.map((t) => (
              <Radio key={t.id} value={String(t.id)} label={t.name} description={t.description ?? undefined} />
            ))}
          </Stack>
        </RadioGroup>
        <Textarea
          label={needsNote ? 'Note' : 'Note (optional)'}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value.slice(0, maxNote))}
          autosize
          minRows={2}
          description={`${note.length} of ${maxNote} characters`}
          required={needsNote}
        />
        {Number(typeId) === F.Declined && (
          <Alert color="grape">A leader will review this. Please respect a request not to be contacted.</Alert>
        )}
        {confirmHandOver && (
          <Alert color="orange" title="Not met at church yet">
            <Stack gap={6}>
              <Text size="sm">They have not been marked as met at church yet. Hand them over anyway?</Text>
              <Group gap="sm">
                <Button size="xs" color="orange" loading={save.isPending} onClick={() => save.mutate(true)}>Hand over anyway</Button>
                <Button size="xs" variant="default" onClick={() => setConfirmHandOver(false)}>Not yet</Button>
              </Group>
            </Stack>
          </Alert>
        )}
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { setError(null); save.mutate(false); }} loading={save.isPending} disabled={!typeId || (needsNote && !note.trim())}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------- correct details

export function EditDetailsModal({ person, opened, onClose }: { person: SalvationConvertDetailView; opened: boolean; onClose: () => void }) {
  const admin = useSalvationAdminService();
  const refresh = useRefreshPerson();
  const { data: lookups } = useSalvationLookups();
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', decisionDate: '', decisionSourceID: null as string | null });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setForm({
      firstName: person.firstName,
      lastName: person.lastName ?? '',
      phone: person.phoneDisplay ?? '',
      email: person.email ?? '',
      decisionDate: person.decisionDate,
      decisionSourceID: person.decisionSourceID ? String(person.decisionSourceID) : null,
    });
    setError(null);
  }, [opened, person]);

  const save = useMutation({
    mutationFn: () =>
      admin.updatePerson(person.convertID, {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        email: form.email,
        decisionDate: form.decisionDate || null,
        decisionSourceID: form.decisionSourceID ? Number(form.decisionSourceID) : null,
        expectedRowVersion: person.rowVersion,
      }),
    onSuccess: () => {
      refresh(person.convertID);
      toast.success('Saved', 'Their details are updated.');
      onClose();
    },
    onError: (err) => {
      setError(salvationErrorMessage(err));
      toast.error("Couldn't save their details", salvationErrorMessage(err));
    },
  });

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  return (
    <Modal opened={opened} onClose={onClose} title="Correct their details" size="lg">
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="First name" value={form.firstName} onChange={(e) => set({ firstName: e.currentTarget.value })} required maxLength={100} />
          <TextInput label="Surname" value={form.lastName} onChange={(e) => set({ lastName: e.currentTarget.value })} maxLength={100} />
          <TextInput label="Phone" value={form.phone} onChange={(e) => set({ phone: e.currentTarget.value })} maxLength={30} />
          <TextInput label="Email" value={form.email} onChange={(e) => set({ email: e.currentTarget.value })} maxLength={256} />
          <TextInput label="Decision date" type="date" value={form.decisionDate} max={todayYmd()} onChange={(e) => set({ decisionDate: e.currentTarget.value })} />
          <Select label="Where they decided" data={lookupOptions(lookups?.decisionSources, person.decisionSourceID)} value={form.decisionSourceID} onChange={(v) => set({ decisionSourceID: v })} clearable />
        </SimpleGrid>
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { setError(null); save.mutate(); }} loading={save.isPending} disabled={!form.firstName.trim()}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------- assign / correct

export function AssignModal({ person, mode, opened, onClose }: {
  person: SalvationConvertDetailView;
  mode: 'assign' | 'correct';
  opened: boolean;
  onClose: () => void;
}) {
  const admin = useSalvationAdminService();
  const refresh = useRefreshPerson();
  const members = useSalvationTeamMembers(opened ? person.teamID : null);
  const [lead, setLead] = useState<string | null>(null);
  const [backup, setBackup] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setLead(person.lead ? String(person.lead.teamMemberID) : null);
    setBackup(person.backup ? String(person.backup.teamMemberID) : null);
    setReason('');
    setError(null);
  }, [opened, person]);

  const save = useMutation({
    mutationFn: () =>
      mode === 'assign'
        ? admin.assign(person.convertID, { leadTeamMemberID: Number(lead), backupTeamMemberID: Number(backup) })
        : admin.correctAssignment(person.convertID, { leadTeamMemberID: Number(lead), backupTeamMemberID: Number(backup), reason }),
    onSuccess: () => {
      refresh(person.convertID);
      toast.success(mode === 'assign' ? 'Assigned' : 'Assignment corrected', 'The Lead and Backup are saved.');
      onClose();
    },
    onError: (err) => {
      setError(salvationErrorMessage(err));
      toast.error("Couldn't save the assignment", salvationErrorMessage(err));
    },
  });

  const valid = lead && backup && lead !== backup && (mode === 'assign' || reason.trim().length >= 5);
  return (
    <Modal opened={opened} onClose={onClose} title={mode === 'assign' ? 'Give them a Lead and a Backup' : 'Correct the assignment'} size="md">
      <Stack gap="sm">
        {mode === 'correct' && (
          <Text size="sm" c="dimmed">
            The Lead and Backup are permanent. Change them only when you have to; the reason is kept with the change.
          </Text>
        )}
        <Select label="Lead" data={members.options} value={lead} onChange={setLead} searchable required />
        <Select label="Backup" data={members.options.filter((o) => o.value !== lead)} value={backup} onChange={setBackup} searchable required />
        {mode === 'correct' && (
          <Textarea label="Reason" placeholder="For example: the Lead is away for two months" value={reason} onChange={(e) => setReason(e.currentTarget.value)} autosize minRows={2} maxLength={500} required />
        )}
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { setError(null); save.mutate(); }} loading={save.isPending} disabled={!valid}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------- reopen / close / review

type ReasonKind = 'reopen' | 'close' | 'review';

const REASON_COPY: Record<ReasonKind, { title: string; label: string; button: string; placeholder: string; required: boolean }> = {
  reopen: { title: 'Reopen', label: 'Reason', button: 'Reopen', placeholder: 'For example: handed over by mistake', required: true },
  close: { title: 'Close', label: 'Reason', button: 'Close', placeholder: 'For example: a duplicate of another record', required: true },
  review: { title: 'Mark as reviewed', label: 'What was decided (optional)', button: 'Mark as reviewed', placeholder: 'For example: respecting their request', required: false },
};

export function ReasonModal({ person, kind, opened, onClose }: { person: SalvationConvertDetailView; kind: ReasonKind; opened: boolean; onClose: () => void }) {
  const admin = useSalvationAdminService();
  const refresh = useRefreshPerson();
  const { data: lookups } = useSalvationLookups();
  const [reason, setReason] = useState('');
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const copy = REASON_COPY[kind];

  useEffect(() => {
    if (!opened) return;
    setReason('');
    setTarget(null);
    setError(null);
  }, [opened]);

  const targets = useMemo(
    () => (lookups?.statuses ?? []).filter((s) => (s.id >= S.NotContacted && s.id <= S.HandedOver) && s.id !== person.statusID)
      .map((s) => ({ value: String(s.id), label: s.name })),
    [lookups, person.statusID]
  );

  const save = useMutation({
    mutationFn: () => {
      if (kind === 'reopen') return admin.reopen(person.convertID, { reason, toStatusID: target ? Number(target) : null });
      if (kind === 'close') return admin.close(person.convertID, { reason });
      return admin.reviewDone(person.convertID, { reason: reason || null });
    },
    onSuccess: () => {
      refresh(person.convertID);
      toast.success(copy.button, 'Saved in their history.');
      onClose();
    },
    onError: (err) => {
      setError(salvationErrorMessage(err));
      toast.error("Couldn't save", salvationErrorMessage(err));
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title={`${copy.title}: ${person.fullName}`} size="md">
      <Stack gap="sm">
        {kind === 'reopen' && (
          <Select
            label="Reopen to"
            description="Leave empty for the usual place (met at church after a hand-over; not contacted after a close)."
            data={targets}
            value={target}
            onChange={setTarget}
            clearable
          />
        )}
        <Textarea label={copy.label} placeholder={copy.placeholder} value={reason} onChange={(e) => setReason(e.currentTarget.value)} autosize minRows={2} maxLength={500} required={copy.required} />
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button color={kind === 'close' ? 'red' : undefined} onClick={() => { setError(null); save.mutate(); }} loading={save.isPending}
            disabled={copy.required && reason.trim().length < 5}>
            {copy.button}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
